import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-bar-chart-comparison',
  standalone: true,
  imports: [
    CommonModule,
    NgxChartsModule,
    MatCardModule,
    MatProgressBarModule,
    MatIconModule
  ],
  templateUrl: './bar-chart-comparison.component.html',
  styleUrls: ['./bar-chart-comparison.component.scss']
})
export class BarChartComparisonComponent implements OnInit, OnChanges {
  @Input() universities: any[] = [];
  @Input() metrics: string[] = [];
  @Input() title: string = 'University Comparison';
  @Input() maxEntities: number = 5;

  chartData: any[] = [];
  selectedMetric: string = '';
  availableMetrics: string[] = [];
  view: [number, number] = [400, 420];

  ngOnInit(): void {
    this.updateResponsiveSettings();
    this.processData();
    this.bindResizeHandler();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['universities'] || changes['metrics']) {
      this.processData();
    }
  }

  private bindResizeHandler(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onResize, { passive: true });
      // Initial calculation
      this.onResize();
    }
  }

  private onResize = (): void => {
    this.updateResponsiveSettings();
  };

  private updateResponsiveSettings(): void {
    if (typeof window === 'undefined') return;
    const width = window.innerWidth;
    // Exact widths by device class
    if (width <= 480) {
      // Mobile
      this.view = [200, 420];
    } else if (width <= 900) {
      // Tablet
      this.view = [250, 420];
    } else if (width <= 1280) {
      // Laptop
      this.view = [300, 420];
    } else {
      // Bigger screens
      this.view = [400, 420];
    }
  }

  private processData(): void {
    if (!this.universities || !this.metrics) return;

    // Set available metrics
    this.availableMetrics = this.metrics;

    // Set default selected metric
    if (this.availableMetrics.length > 0 && !this.selectedMetric) {
      this.selectedMetric = this.availableMetrics[0];
    }

    // Prepare chart data for the selected metric
    this.updateChartData();
  }

  onMetricChange(event: any): void {
    const newMetric = event.target.value;
    if (newMetric !== this.selectedMetric) {
      this.selectedMetric = newMetric;
      this.updateChartData();
    }
  }

  getAverageScore(): string {
    if (this.chartData.length === 0) return '0';
    const total = this.chartData.reduce((sum, item) => sum + item.value, 0);
    return (total / this.chartData.length).toFixed(1);
  }

  getScoreRange(): string {
    if (this.chartData.length === 0) return '0 - 0';
    const min = this.chartData[this.chartData.length - 1].value;
    const max = this.chartData[0].value;
    return `${min} - ${max}`;
  }

  private updateChartData(): void {
    if (!this.selectedMetric || !this.universities) return;

    const limitedUniversities = this.universities.slice(0, this.maxEntities);

    this.chartData = limitedUniversities.map(uni => {
      const metricValue = uni[this.selectedMetric] || uni.series?.find((s: any) => s.name === this.selectedMetric)?.value || 0;
      return {
        name: uni.name || uni.university,
        value: this.parseMetricValue(metricValue)
      };
    }).sort((a, b) => b.value - a.value); // Sort by value descending
  }

  private parseMetricValue(value: any): number {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      // Handle percentage strings like "92%"
      if (value.includes('%')) {
        const numericValue = parseFloat(value.replace('%', ''));
        return isNaN(numericValue) ? 0 : numericValue;
      }

      // Handle currency strings like "£43,950"
      if (value.includes('£')) {
        const numericValue = parseFloat(value.replace(/[£,]/g, ''));
        return isNaN(numericValue) ? 0 : numericValue;
      }

      // Handle regular numeric strings
      const numericValue = parseFloat(value);
      return isNaN(numericValue) ? 0 : numericValue;
    }

    return 0;
  }

  getMetricColor(value: number): string {
    if (value >= 80) return '#4caf50'; // Green for high scores
    if (value >= 60) return '#ff9800'; // Orange for medium scores
    return '#f44336'; // Red for low scores
  }

  getMetricIcon(value: number): string {
    if (value >= 80) return 'star';
    if (value >= 60) return 'check_circle';
    return 'warning';
  }
}
